/**
 * Allenamenti della squadra-stagione (V10), a calendario mensile o in elenco.
 *
 * Si apre sull'elenco, che mette in testa i programmati rimasti
 * indietro, poi i prossimi dal piu vicino e i passati dal piu recente, ogni
 * gruppo nella sua card e cinque righe per pagina; prossimi e passati si
 * affiancano solo se ci sono tutti e due. Il filtro per stato vale
 * per entrambe le viste; il mese no: l'elenco si apre su tutti i mesi e il
 * calendario sul mese corrente. Vista, mese, giorno scelto e pagine stanno
 * nell'URL. La testata riassume la stagione. Senza allenamenti la vista
 * porta alla creazione in blocco, che e come si parte davvero.
 */

import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarClockIcon,
  CalendarRangeIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  HistoryIcon,
  type LucideIcon,
  UsersIcon,
} from 'lucide-react';
import { dataLocale, puo } from '@calcio/shared';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { AzioniTestata, MenuAzioni, PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import {
  Filtri,
  Filtro,
  GruppoPaginato,
  Riepilogo,
  TUTTI,
  VoceRiepilogo,
} from '../comuni/FiltriVista.js';
import { useDati } from '../comuni/dati.js';
import { type GiornoProposto, meseDi, meseIniziale, nomeMese } from '../comuni/calendario.js';
import { useContesto } from '../navigazione/contesto.js';
import {
  type AllenamentoInElenco,
  ETICHETTA_STATO,
  raggruppa,
  riepilogoStagione,
  segnoDi,
} from './allenamenti.js';
import { DialogBlocco } from './BloccoAllenamenti.js';
import { CalendarioAllenamenti } from './CalendarioAllenamenti.js';
import { DialogAllenamento } from './FormAllenamento.js';
import { RigaAllenamento } from './RigaAllenamento.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

/** Le chiavi della pagina di ogni gruppo nell'URL: un filtro nuovo le azzera. */
const PAGINE = { daChiudere: 'da-chiudere', prossimi: 'prossimi', passati: 'passati' } as const;
const SENZA_PAGINE = Object.fromEntries(Object.values(PAGINE).map((k) => [k, null]));

export function ElencoAllenamenti() {
  const { ss } = useParams<{ ss: string }>();
  // Il componente si rimonta cambiando squadra: i filtri della precedente
  // non hanno senso sull'altra.
  return <Elenco key={ss} ss={ss!} />;
}

function Elenco({ ss }: { ss: string }) {
  const { utente, squadre, erroreSquadre } = useContesto();
  const { dati, errore, ricarica } = useDati<AllenamentoInElenco[]>(
    `/squadre-stagione/${ss}/allenamenti`,
  );
  const [params, setParams] = useSearchParams();
  const [stato, setStato] = useState<string>(TUTTI);
  /** Il modale del nuovo allenamento: aperto, con il giorno scelto nel calendario se c'e. */
  const [nuovo, setNuovo] = useState<{ giorno?: GiornoProposto } | null>(null);
  const [blocco, setBlocco] = useState(false);
  const calendario = params.get('vista') === 'calendario';
  const mese = params.get('mese') ?? TUTTI;
  const stagione = squadre?.find((s) => s.id === ss);
  const gestisce = puo(utente.assegnazioni, 'allenamento.gestisci', ss);
  const adesso = new Date();

  const testata = (
    <Testata
      titolo="Allenamenti"
      voce="allenamenti"
      accanto={dati && dati.length > 0 && <RiepilogoAllenamenti elenco={dati} adesso={adesso} />}
      azione={
        gestisce && (
          <AzioniTestata>
            <PulsanteNuovo etichetta="Nuovo allenamento" onClick={() => setNuovo({})} />
            <MenuAzioni
              voci={[
                {
                  etichetta: 'In blocco',
                  Icona: CalendarRangeIcon,
                  onSelect: () => setBlocco(true),
                },
              ]}
            />
          </AzioniTestata>
        )
      }
    />
  );

  const modale = gestisce && (
    <>
      <DialogAllenamento
        ss={ss}
        aperto={nuovo !== null}
        giorno={nuovo?.giorno}
        onChiudi={() => setNuovo(null)}
      />
      <DialogBlocco ss={ss} aperto={blocco} onChiudi={() => setBlocco(false)} onCreati={ricarica} />
    </>
  );

  // Il calendario si ferma ai mesi della stagione, che arrivano col contesto.
  if (!dati || (calendario && !stagione)) {
    return (
      <div>
        {testata}
        {modale}
        <Corpo>
          <StatoCaricamento errore={errore ?? (calendario ? erroreSquadre : null)} />
        </Corpo>
      </div>
    );
  }

  if (dati.length === 0) {
    return (
      <div>
        {testata}
        {modale}
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessun allenamento</EmptyTitle>
            <EmptyDescription>
              {gestisce
                ? 'Programma il periodo in un colpo: giorni della settimana, ora e luogo, con l’anteprima delle date prima di salvare.'
                : 'Mister e Dirigente non hanno ancora programmato allenamenti.'}
            </EmptyDescription>
          </EmptyHeader>
          {gestisce && (
            <EmptyContent>
              <Button onClick={() => setBlocco(true)}>
                <CalendarRangeIcon data-icon="inline-start" />
                Programma il periodo
              </Button>
            </EmptyContent>
          )}
        </Empty>
      </div>
    );
  }

  /** Cambia i parametri nell'URL senza allungare la cronologia a ogni tocco. */
  const aggiorna = (valori: Record<string, string | null>) => {
    const q = new URLSearchParams(params);
    for (const [chiave, valore] of Object.entries(valori)) {
      if (valore === null || valore === TUTTI) q.delete(chiave);
      else q.set(chiave, valore);
    }
    setParams(q, { replace: true });
  };

  const oggi = dataLocale(adesso);
  const perStato = dati.filter((a) => stato === TUTTI || a.stato === stato);
  const meseCalendario = stagione ? meseIniziale(stagione, adesso, mese) : null;

  const cambiaVista = (vista: 'calendario' | 'elenco') => {
    // Il mese non passa da una vista all'altra: il calendario riparte dal
    // mese corrente, l'elenco da tutti i mesi.
    if (vista === 'calendario') aggiorna({ vista, mese: null, ...SENZA_PAGINE });
    else aggiorna({ vista: null, mese: null, giorno: null });
  };

  const filtroStato = (
    <Filtro
      etichetta="Stato"
      valore={stato}
      onCambia={(s) => {
        setStato(s);
        aggiorna(SENZA_PAGINE);
      }}
      opzioni={[
        { valore: TUTTI, etichetta: 'Tutti' },
        ...Object.entries(ETICHETTA_STATO).map(([valore, etichetta]) => ({
          valore,
          etichetta,
        })),
      ]}
    />
  );

  if (calendario && stagione && meseCalendario) {
    return (
      <div>
        {testata}
        <Corpo className="flex flex-col gap-4">
          <Filtri calendario={calendario} onVista={cambiaVista}>
            {filtroStato}
          </Filtri>
          <CalendarioAllenamenti
            ss={ss}
            stagione={stagione}
            tutti={dati}
            filtrati={perStato}
            mese={meseCalendario}
            giorno={params.get('giorno')}
            gestisce={gestisce}
            adesso={adesso}
            onMese={(m) => aggiorna({ mese: m, giorno: null })}
            onGiorno={(g) => aggiorna({ mese: meseCalendario, giorno: g })}
            onNuovo={(giorno) => setNuovo({ giorno })}
          />
          {modale}
        </Corpo>
      </div>
    );
  }

  // Un mese arrivato dal calendario puo non avere allenamenti: resta tra le voci.
  const mesi = [
    ...new Set([...dati.map((a) => meseDi(a.dataOra)), ...(mese === TUTTI ? [] : [mese])]),
  ].sort();
  const filtrati = perStato.filter((a) => mese === TUTTI || meseDi(a.dataOra) === mese);
  const g = raggruppa(filtrati, adesso);

  return (
    <div>
      {testata}
      {modale}
      <Corpo className="flex flex-col gap-4">
        <Filtri calendario={calendario} onVista={cambiaVista}>
          {filtroStato}
          <Filtro
            etichetta="Mese"
            valore={mese}
            onCambia={(m) => aggiorna({ mese: m, ...SENZA_PAGINE })}
            opzioni={[
              { valore: TUTTI, etichetta: 'Tutti' },
              ...mesi.map((m) => ({ valore: m, etichetta: nomeMese(m) })),
            ]}
          />
        </Filtri>

        {filtrati.length === 0 && <StatoVuoto titolo="Nessun allenamento con questi filtri" />}

        <GruppoAllenamenti
          titolo="Da chiudere"
          Icona={CircleAlertIcon}
          colore="text-stato-recupero"
          nota="Programmati con la data già passata: segnali svolti facendo l'appello, o annullali."
          allenamenti={g.daChiudere}
          chiave={PAGINE.daChiudere}
          oggi={oggi}
          ss={ss}
        />
        <div
          className={cn(
            'grid gap-4',
            g.prossimi.length > 0 && g.passati.length > 0 && '@5xl:grid-cols-2',
          )}
        >
          <GruppoAllenamenti
            titolo="Prossimi"
            Icona={CalendarClockIcon}
            allenamenti={g.prossimi}
            chiave={PAGINE.prossimi}
            oggi={oggi}
            ss={ss}
          />
          <GruppoAllenamenti
            titolo="Passati"
            Icona={HistoryIcon}
            allenamenti={g.passati}
            chiave={PAGINE.passati}
            oggi={oggi}
            ss={ss}
          />
        </div>
      </Corpo>
    </div>
  );
}

/** I numeri della stagione accanto al titolo. */
function RiepilogoAllenamenti({ elenco, adesso }: { elenco: AllenamentoInElenco[]; adesso: Date }) {
  const r = riepilogoStagione(elenco, adesso);
  return (
    <Riepilogo>
      <VoceRiepilogo
        Icona={CircleCheckIcon}
        valore={r.svolti}
        titolo="Svolti"
        colore="text-stato-disponibile"
      />
      <VoceRiepilogo Icona={CalendarClockIcon} valore={r.inProgramma} titolo="In programma" />
      {r.daChiudere > 0 && (
        <VoceRiepilogo
          Icona={CircleAlertIcon}
          valore={r.daChiudere}
          titolo="Da chiudere"
          colore="text-stato-recupero"
        />
      )}
      {r.presenza !== null && (
        <VoceRiepilogo
          Icona={UsersIcon}
          valore={`${r.presenza}%`}
          titolo="Presenza media agli svolti"
        />
      )}
    </Riepilogo>
  );
}

function GruppoAllenamenti({
  allenamenti,
  oggi,
  ss,
  ...gruppo
}: {
  titolo: string;
  Icona: LucideIcon;
  colore?: string;
  nota?: string;
  allenamenti: AllenamentoInElenco[];
  chiave: string;
  oggi: string;
  ss: string;
}) {
  return (
    <GruppoPaginato
      {...gruppo}
      elementi={allenamenti}
      riga={(a) => <RigaAllenamento allenamento={a} segno={segnoDi(a, oggi)} ss={ss} />}
    />
  );
}
